/**
 * 指文字（五十音）の画像と説明文。
 * 画像は public/fingerspelling/{side}/{id}.png に配置している。手と矢印だけで、かな・説明文は含まない。
 * public/fingerspelling/yurai/{id}.png は由来で覚える用（手 + 薄いオレンジのヒント、相手編のみ）で、
 * ゃゅょっ・濁点・半濁点・長音にはない。
 */

/** 相手から見た形（aite）か、自分から見た形（jibun）か */
export type FingerspellingSide = "aite" | "jibun";

/** 説明文の1区切り。ruby があればふりがなとして表示する */
export type CaptionSegment = { text: string; ruby?: string };

export type FingerspellingEntry = {
  /** 画像ファイル名（拡張子なし） */
  id: string;
  kana: string;
  caption: Record<FingerspellingSide, CaptionSegment[]>;
};

export const FINGERSPELLING_SIDES: readonly FingerspellingSide[] = [
  "aite",
  "jibun",
];

export const FINGERSPELLING_SIDE_LABELS: Record<FingerspellingSide, string> = {
  aite: "あいてから見た手",
  jibun: "じぶんから見た手",
};

const t = (text: string): CaptionSegment => ({ text });
const r = (text: string, ruby: string): CaptionSegment => ({ text, ruby });

/** 相手編・自分編で同じ説明文 */
const same = (
  ...segments: CaptionSegment[]
): Record<FingerspellingSide, CaptionSegment[]> => ({
  aite: segments,
  jibun: segments,
});

const alphabet = (letter: string) => same(t(`アルファベットの「${letter}」`));
const katakana = (letter: string) => same(t(`カタカナの「${letter}」`));
const shuwaNumber = (numeral: CaptionSegment) =>
  same(
    r("手話", "しゅわ"),
    t("の"),
    r("数詞", "すうし"),
    t("の「"),
    numeral,
    t("」"),
  );
const keishou = (...subject: CaptionSegment[]) =>
  same(...subject, t("を"), r("形象", "けいしょう"), t("する"));

/** を・ゃ・ゅ・ょ・っ：相手編は「後ろへ引く」、自分編は「手前へ引く」 */
const pull = (): Record<FingerspellingSide, CaptionSegment[]> => ({
  aite: [r("後", "うし"), t("ろへ"), r("引", "ひ"), t("く")],
  jibun: [r("手前", "てまえ"), t("へ"), r("引", "ひ"), t("く")],
});

export const FINGERSPELLING_ENTRIES: readonly FingerspellingEntry[] = [
  { id: "a", kana: "あ", caption: alphabet("a") },
  { id: "i", kana: "い", caption: alphabet("i") },
  { id: "u", kana: "う", caption: alphabet("u") },
  { id: "e", kana: "え", caption: alphabet("e") },
  { id: "o", kana: "お", caption: alphabet("o") },
  { id: "ka", kana: "か", caption: alphabet("k") },
  { id: "ki", kana: "き", caption: same(t("きつねの「き」")) },
  { id: "ku", kana: "く", caption: shuwaNumber(r("九", "く")) },
  { id: "ke", kana: "け", caption: alphabet("B") },
  {
    id: "ko",
    kana: "こ",
    caption: same(t("カタカナの「コ」の"), r("一部", "いちぶ")),
  },
  { id: "sa", kana: "さ", caption: alphabet("s") },
  { id: "shi", kana: "し", caption: shuwaNumber(t("七")) },
  { id: "su", kana: "す", caption: katakana("ス") },
  {
    id: "se",
    kana: "せ",
    caption: same(
      t("「兄（せ）」の"),
      r("敬称", "けいしょう"),
      t("「"),
      r("背", "せ"),
      t("の"),
      r("君", "きみ"),
      t("」"),
    ),
  },
  { id: "so", kana: "そ", caption: same(t("「それ」の「そ」")) },
  { id: "ta", kana: "た", caption: alphabet("t") },
  { id: "chi", kana: "ち", caption: katakana("チ") },
  { id: "tsu", kana: "つ", caption: katakana("ツ") },
  {
    id: "te",
    kana: "て",
    caption: same(r("手", "て"), t("そのものを"), r("表", "あらわ"), t("す")),
  },
  {
    id: "to",
    kana: "と",
    caption: same(t("「あなたと"), r("私", "わたし"), t("」の「と」")),
  },
  { id: "na", kana: "な", caption: alphabet("n") },
  { id: "ni", kana: "に", caption: katakana("ニ") },
  { id: "nu", kana: "ぬ", caption: same(t("「ぬすむ」の「ぬ」")) },
  {
    id: "ne",
    kana: "ね",
    caption: same(r("木", "き"), t("の"), r("根", "ね"), t("の「ね」")),
  },
  { id: "no", kana: "の", caption: katakana("ノ") },
  { id: "ha", kana: "は", caption: alphabet("h") },
  { id: "hi", kana: "ひ", caption: shuwaNumber(t("1")) },
  { id: "fu", kana: "ふ", caption: katakana("フ") },
  { id: "he", kana: "へ", caption: katakana("ヘ") },
  {
    id: "ho",
    kana: "ほ",
    caption: keishou(t("「"), r("舟", "ふね"), t("の"), r("帆", "ほ"), t("」")),
  },
  { id: "ma", kana: "ま", caption: alphabet("m") },
  { id: "mi", kana: "み", caption: shuwaNumber(r("三", "さん")) },
  { id: "mu", kana: "む", caption: shuwaNumber(r("六", "ろく")) },
  { id: "me", kana: "め", caption: keishou(t("「"), r("目", "め"), t("」")) },
  {
    id: "mo",
    kana: "も",
    caption: same(
      r("手話", "しゅわ"),
      t("の「"),
      r("同", "おな"),
      t("じ」を"),
      r("示", "しめ"),
      t("す"),
    ),
  },
  { id: "ya", kana: "や", caption: alphabet("Y") },
  {
    id: "yu",
    kana: "ゆ",
    caption: keishou(t("「"), r("湯気", "ゆげ"), t("」")),
  },
  { id: "yo", kana: "よ", caption: shuwaNumber(r("四", "よん")) },
  { id: "ra", kana: "ら", caption: alphabet("r") },
  { id: "ri", kana: "り", caption: katakana("リ") },
  { id: "ru", kana: "る", caption: katakana("ル") },
  { id: "re", kana: "れ", caption: katakana("レ") },
  { id: "ro", kana: "ろ", caption: katakana("ロ") },
  { id: "wa", kana: "わ", caption: alphabet("w") },
  { id: "wo", kana: "を", caption: pull() },
  { id: "n", kana: "ん", caption: katakana("ン") },
  {
    id: "dakuten",
    kana: "゛",
    caption: same(
      r("横", "よこ"),
      t("に"),
      r("移動", "いどう"),
      t("する（例「ぶ」）"),
    ),
  },
  {
    id: "handakuten",
    kana: "゜",
    caption: same(
      r("上", "うえ"),
      t("に"),
      r("移動", "いどう"),
      t("する（例「ぱ」）"),
    ),
  },
  { id: "small-ya", kana: "ゃ", caption: pull() },
  { id: "small-yu", kana: "ゅ", caption: pull() },
  { id: "small-yo", kana: "ょ", caption: pull() },
  { id: "small-tsu", kana: "っ", caption: pull() },
  {
    id: "choon",
    kana: "ー",
    caption: same(t("「ー」を"), r("空書", "そらがき"), t("する")),
  },
];

export const getFingerspellingImageUrl = (
  side: FingerspellingSide,
  id: string,
): string => `/fingerspelling/${side}/${id}.png`;

/** 由来で覚える用（手 + ヒント）の画像。相手から見た手のみで、一部の id にはない */
export const getYuraiImageUrl = (id: string): string =>
  `/fingerspelling/yurai/${id}.png`;

/** 説明文をふりがななしのプレーンテキストにする */
export const toPlainCaption = (segments: readonly CaptionSegment[]): string =>
  segments.map((segment) => segment.text).join("");
